import React, { useContext, useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Globe, Shield, RefreshCw, AlertTriangle, 
  CheckCircle2, XCircle, Lock, BarChart2 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { useLoading } from '../hooks/useLoading';
import { AssetsApi, Domain } from 'api-client';
import { getApiConfiguration } from '../api/utils';
import { AuthContext } from '../contexts/AuthContext';
import { useTeams } from '../contexts/TeamsContext';

// Import mock data for email security
import { emailDomainStatuses, threatStats, securityAlerts } from '../data/email-security';

interface SecurityCheck {
  name: string;
  status: 'success' | 'warning' | 'error';
  description: string;
  lastChecked: string;
}

interface ThreatMetric {
  name: string;
  value: string;
  change: string;
  status: 'success' | 'warning' | 'error';
}

const SecurityCenterDetail = () => {
  const { type, domain } = useParams<{ type: string; domain: string }>();
  const navigate = useNavigate();
  const { isLoading, withLoading } = useLoading();
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  
  const [domainData, setDomainData] = useState<Domain | null>(null);
  const [emailData, setEmailData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch domain data from API
  const fetchDomainData = useCallback(async () => {
    if (!currentTeam?.slug || !token || !domain) return;

    try {
      const api = new AssetsApi(getApiConfiguration(token));
      const response = await api.domainsList({
        teamSlug: currentTeam.slug,
        page: 1,
        search: domain
      });
      
      const foundDomain = response.results.find(d => d.domainName === domain);
      if (foundDomain) {
        setDomainData(foundDomain);
      } else {
        setError('Domain not found');
      }
    } catch (err) {
      console.error('Error fetching domain data:', err);
      setError('Failed to fetch domain data. Please try again later.');
    }
  }, [currentTeam?.slug, token, domain]);

  interface ScanAdvice {
    advice: string;
    effort: string;
    priority: string;
  }
  interface DomainScanData {
    tls_certificate_status?: string;
    ns_status?: string;
    spf_status?: string;
    dmarc_status?: string;
    tls_advice?: ScanAdvice[];
    ns_advice?: ScanAdvice[];
    spf_advice?: ScanAdvice[];
    dmarc_advice?: ScanAdvice[];
  }

  // Helper function to parse the latestScan JSON string
  const parseLatestScan = (scanData: string | null | undefined): DomainScanData | null => {
    if (!scanData) return null;
    
    try {
      // If scanData is already an object, return it as is
      const parsed = typeof scanData === 'string' ? JSON.parse(scanData) : scanData;
      return parsed as DomainScanData;
    } catch (error) {
      console.error('Error parsing scan data:', error);
      return null;
    }
  };

  // Fetch email data from mock data
  const fetchEmailData = useCallback(() => {
    if (!domain) return;
    
    const foundEmailDomain = emailDomainStatuses.find(d => d.domain === domain);
    if (foundEmailDomain) {
      setEmailData(foundEmailDomain);
    } else {
      setError('Email domain not found');
    }
  }, [domain]);

  useEffect(() => {
    if (type === 'domain') {
      fetchDomainData();
    } else if (type === 'email') {
      fetchEmailData();
    }
  }, [type, fetchDomainData, fetchEmailData]);

  const handleScan = async () => {
    await withLoading(async () => {
      if (type === 'domain') {
        await fetchDomainData();
      } else if (type === 'email') {
        fetchEmailData();
      }
      console.log(`Scanning ${type} security for ${domain}...`);
    });
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'text-gray-400';
    
    switch (status.toLowerCase()) {
      case 'configured':
      case 'valid':
      case 'pass':
      case 'secure':
      case 'enforced':
        return 'text-emerald-500';
      case 'warning':
      case 'insecure':
      case 'quarantine':
        return 'text-yellow-500';
      case 'not_configured':
      case 'invalid':
      case 'expired':
      case 'none':
      case 'missing':
        return 'text-red-500';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: 'success' | 'warning' | 'error') => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
    }
  };

  // Generate security checks based on type and data
  const getSecurityChecks = (): SecurityCheck[] => {
    if (type === 'domain' && domainData) {
      const lastScan = parseLatestScan(domainData.latestScan);
      return [
        {
          name: 'TLS Certificate',
          status: lastScan?.tls_certificate_status === 'valid' ? 'success' : 
                  lastScan?.tls_certificate_status === 'warning' ? 'warning' : 'error',
          description: lastScan?.tls_certificate_status === 'valid'
            ? 'TLS certificate is valid and properly configured'
            : 'TLS certificate needs attention',
          lastChecked: '30 minutes ago'
        },
        {
          name: 'NS Status',
          status: lastScan?.ns_status === 'configured' ? 'success' : 
                  lastScan?.ns_status === 'warning' ? 'warning' : 'error',
          description: lastScan?.ns_status === 'configured'
            ? 'Name servers are properly configured'
            : 'Name server configuration needs attention',
          lastChecked: '45 minutes ago'
        },
        {
          name: 'SPF Record',
          status: lastScan?.spf_status === 'configured' ? 'success' : 
                  lastScan?.spf_status === 'warning' ? 'warning' : 'error',
          description: lastScan?.spf_status === 'configured'
            ? 'SPF record is properly configured'
            : 'SPF record needs attention',
          lastChecked: '1 hour ago'
        },
        {
          name: 'DMARC Policy',
          status: lastScan?.dmarc_status === 'configured' ? 'success' : 
                  lastScan?.dmarc_status === 'warning' ? 'warning' : 'error',
          description: lastScan?.dmarc_status === 'configured'
            ? 'DMARC policy is properly configured'
            : 'DMARC policy needs attention',
          lastChecked: '2 hours ago'
        }
      ];
    } else if (type === 'email' && emailData) {
      return [
        {
          name: 'SPF Record',
          status: emailData.spf === 'pass' ? 'success' : 'error',
          description: emailData.spf === 'pass'
            ? 'SPF record is properly configured and passing authentication'
            : 'SPF record is failing authentication checks',
          lastChecked: '30 minutes ago'
        },
        {
          name: 'DKIM',
          status: emailData.dkim === 'configured' ? 'success' : 'error',
          description: emailData.dkim === 'configured'
            ? 'DKIM is properly configured and signing emails'
            : 'DKIM is not configured or missing',
          lastChecked: '45 minutes ago'
        },
        {
          name: 'DMARC Policy',
          status: emailData.dmarc === 'enforced' ? 'success' : emailData.dmarc === 'quarantine' ? 'warning' : 'error',
          description: emailData.dmarc === 'enforced'
            ? 'DMARC policy is enforced with reject policy'
            : emailData.dmarc === 'quarantine'
              ? 'DMARC policy is set to quarantine suspicious emails'
              : 'DMARC policy is not configured',
          lastChecked: '1 hour ago'
        },
        {
          name: 'MTA-STS',
          status: emailData.mta === 'secure' ? 'success' : 'warning',
          description: emailData.mta === 'secure'
            ? 'MTA-STS is properly configured for secure mail transfer'
            : 'MTA-STS configuration needs attention',
          lastChecked: '2 hours ago'
        }
      ];
    }
    return [];
  };

  // Generate threat metrics
  const threatMetrics: ThreatMetric[] = threatStats.map(stat => ({
    name: stat.type,
    value: stat.count.toString(),
    change: stat.trend,
    status: stat.status === 'warning' ? 'warning' : 'success'
  }));

  // If data is not found
  if ((type === 'domain' && !domainData) || (type === 'email' && !emailData)) {
    if (isLoading) {
      return <LoadingOverlay message={`Loading ${type} security data...`} />;
    }
    
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
          <AlertTriangle className="h-12 w-12 text-yellow-500" />
          <h2 className="text-xl font-semibold text-white">{error}</h2>
          <Button variant="outline" onClick={() => navigate('/security-center')}>
            Back to Security Center
          </Button>
        </div>
      );
    }
    
    return null;
  }

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message={`Scanning ${type} security...`} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/security-center')}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center space-x-3">
            {type === 'domain' ? (
              <Globe className="h-8 w-8 text-blue-500" />
            ) : (
              <Mail className="h-8 w-8 text-blue-500" />
            )}
            <div>
              <h1 className="text-2xl font-bold text-white">{domain}</h1>
              <p className="text-sm text-gray-400">
                {type === 'domain' ? 'Domain Security Details' : 'Email Security Details'}
              </p>
            </div>
          </div>
        </div>
        <Button onClick={handleScan} className="flex items-center space-x-2">
          <RefreshCw className="h-4 w-4 mr-2" />
          Scan Now
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Security Checks */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Security Checks</h2>
          <div className="space-y-4">
            {getSecurityChecks().map((check) => (
              <div
                key={check.name}
                className="p-4 bg-gray-800/30 rounded-lg space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(check.status)}
                    <span className="text-sm font-medium text-white">
                      {check.name}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {check.lastChecked}
                  </span>
                </div>
                <p className="text-sm text-gray-400">{check.description}</p>
              </div>
            ))}
          </div>
        </div>


                  {/* Recommendations Section */}
      {type === 'domain' && domainData && (
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Security Recommendations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(() => {
              const scanData = parseLatestScan(domainData.latestScan);
              return (
                <React.Fragment>
                  {/* TLS Recommendations */}
                  {scanData?.tls_advice && scanData.tls_advice.length > 0 && (
                    <div className="bg-gray-800/30 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <Lock className="h-5 w-5 text-blue-500" />
                        <h3 className="text-md font-medium text-white">TLS Recommendations</h3>
                      </div>
                      <div className="space-y-3">
                        {scanData.tls_advice.map((advice, index) => (
                          <div key={index} className="bg-gray-800/50 rounded-lg p-3">
                            <div className="text-sm text-white mb-2">{advice?.advice || 'No advice available'}</div>
                            <span className="text-xs text-gray-400">Effort: {advice?.effort || 'Unknown'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* SPF Recommendations */}
                  {scanData?.spf_advice && scanData.spf_advice.length > 0 && (
                    <div className="bg-gray-800/30 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <Shield className="h-5 w-5 text-yellow-500" />
                        <h3 className="text-md font-medium text-white">SPF Recommendations</h3>
                      </div>
                      <div className="space-y-3">
                        {scanData.spf_advice.map((advice, index) => (
                          <div key={index} className="bg-gray-800/50 rounded-lg p-3">
                            <div className="text-sm text-white mb-2">{advice?.advice || 'No advice available'}</div>
                            <span className="text-xs text-gray-400">Effort: {advice?.effort || 'Unknown'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* DMARC Recommendations */}
                  {scanData?.dmarc_advice && scanData.dmarc_advice.length > 0 && (
                    <div className="bg-gray-800/30 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <Shield className="h-5 w-5 text-purple-500" />
                        <h3 className="text-md font-medium text-white">DMARC Recommendations</h3>
                      </div>
                      <div className="space-y-3">
                        {scanData.dmarc_advice.map((advice, index) => (
                          <div key={index} className="bg-gray-800/50 rounded-lg p-3">
                            <div className="text-sm text-white mb-2">{advice?.advice || 'No advice available'}</div>
                            <span className="text-xs text-gray-400">Effort: {advice?.effort || 'Unknown'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })()}
          </div>
        </div>
      )}
        {/* Threat Metrics and Alerts 
        <div className="space-y-6">
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Threat Metrics</h2>
            <div className="space-y-4">
              {threatMetrics.map((metric) => (
                <div
                  key={metric.name}
                  className="p-4 bg-gray-800/30 rounded-lg flex items-center justify-between"
                >
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-white">{metric.name}</div>
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl font-semibold text-white">
                        {metric.value}
                      </span>
                      <span className={`text-sm ${
                        metric.change.startsWith('+') ? 'text-red-500' : 'text-emerald-500'
                      }`}>
                        {metric.change}
                      </span>
                    </div>
                  </div>
                  {getStatusIcon(metric.status)}
                </div>
              ))}
            </div>
          </div>
          */}

          {/* Recent Alerts 
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Recent Alerts</h2>
            <div className="space-y-4">
              {securityAlerts.map((alert, index) => (
                <div
                  key={index}
                  className="p-4 bg-gray-800/30 rounded-lg flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className={`w-2 h-2 rounded-full ${
                        alert.severity === 'high' ? 'bg-red-500' :
                        alert.severity === 'medium' ? 'bg-yellow-500' :
                        'bg-blue-500'
                      }`} />
                      <span className="text-sm font-medium text-white">
                        {alert.title}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{alert.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        */}
      </div>


    </div>
  );
};

export default SecurityCenterDetail;
